import React, { useEffect, useState } from "react";
import { bool, func, instanceOf, shape, string } from "prop-types";
import { connect, useDispatch, useSelector } from "react-redux";
import { Col, Row } from "react-bootstrap";
import { Field, Form, Formik } from "formik";
import Credential from "pages/_components/fields/credentials/Credential";
import Button from "pages/_components/Button";
import Loader from "pages/_components/Loader";
import { Link } from "react-router-dom";
import { actions as transactionActions } from "reducers/transactions";
import Check from "pages/_components/fields/Checkbox";
import useSchema from "hooks/useSchema";
import * as i18n from "util/i18n";
import * as Yup from "yup";
import { actions, selectors } from "reducers/softToken";
import { actions as notificationActions } from "reducers/notification";

const GeneralCredentialForm = ({
    idActivity,
    transactionData,
    action,
    actionOnReload,
    isDisabled,
    hasTerms,
    hideIndications,
    customIndications,
    signData,
    deviceStatus,
    tokenInfo,
    isLoading,
    messageSnackBar,
    hideButton,
    customClass,
    customSubmitLabel,
    isCancel,
    type,
    disableContinueIfEmpty,
}) => {
    const dispatch = useDispatch();
    const CANCEL_ACTIVITY = "core.cancelTransaction";
    const { requiredCredentials, isFetching } = useSelector((state) => state.transactions);
    const filterCredentials = requiredCredentials.filter((credential) => credential !== "accessToken");
    const credentialType = filterCredentials[0] || type || "otp";
    const { stringRequired } = useSchema();

    const [checkboxState, setCheckboxState] = useState(false);
    const [checkboxError, setCheckboxError] = useState(false);

    const handleCheckboxChange = () => {
        setCheckboxError(false);
        setCheckboxState(!checkboxState);
    };

    useEffect(() => {
        if (isCancel) {
            dispatch(transactionActions.fetchCredentialGroup(CANCEL_ACTIVITY));
        } else if (idActivity) {
            dispatch(transactionActions.fetchCredentialGroup(idActivity));
        }
        dispatch(actions.getTokenInfo());
    }, []);

    if (isFetching) {
        return <Loader />;
    }

    const handleSubmit = ({ credential = "" }, formikBag) => {
        if (messageSnackBar) {
            dispatch(notificationActions.showNotification(messageSnackBar, "error", ["debinActionPay"]));
            formikBag.setSubmitting(false);
        } else {
            const token = credentialType === "otp" && deviceStatus !== "NO_DEVICE" ? tokenInfo.token : credential;

            if (actionOnReload) {
                dispatch(actionOnReload({}));
            }
            const { idTransaction, idActivity: idActivityTrx } = transactionData;
            if (idTransaction) {
                const _signData = signData?.reduce((sum, key) => {
                    // eslint-disable-next-line no-param-reassign
                    sum[key] = transactionData[key];
                    return sum;
                }, {});

                dispatch(
                    action(
                        {
                            idTransaction,
                            idActivity: idActivityTrx,
                            ..._signData,
                            [`_${credentialType}`]: token,
                        },
                        formikBag,
                        credentialType,
                    ),
                );
            } else {
                dispatch(
                    action(
                        {
                            ...transactionData,
                            [`_${credentialType}`]: token,
                        },
                        formikBag,
                        credentialType,
                    ),
                );
            }
        }
    };
    const handleActivation = (values) => {
        let validationSchema;
        if (!hideButton) {
            if (isDisabled && !hasTerms) {
                validationSchema = !values.credential || isDisabled;
            }
            if (deviceStatus === "NO_DEVICE") {
                if (hasTerms) {
                    validationSchema = !checkboxState || !values.credential.length > 0;
                }

                // Deshabilito si no se ingresa token en caso de no tener dispositivo registrado
                if (disableContinueIfEmpty && !values.credential) {
                    validationSchema = true;
                }
            }
        } else {
            validationSchema = hideButton;
        }

        return validationSchema;
    };

    return (
        <Formik
            initialValues={{
                credential: "",
            }}
            validationSchema={Yup.object().shape({
                credential: deviceStatus === "NO_DEVICE" ? stringRequired : Yup.string(),
            })}
            onSubmit={handleSubmit}>
            {({ isSubmitting, values }) => (
                <Form>
                    <Row className={`form-actions ${customClass}`}>
                        {hasTerms && (
                            <div>
                                <Check
                                    hideLabel
                                    name="termsAndConditios"
                                    onChange={handleCheckboxChange}
                                    checked={checkboxState}
                                    error={checkboxError ? "Campo requerido" : ""}
                                    formGroup={false}
                                    block={false}
                                />
                                <span>Aceptá los </span>
                                <Link to="/termsAndConditions">{i18n.get("global.termAndConditions")}</Link>
                            </div>
                        )}
                        {deviceStatus !== "NO_DEVICE" && credentialType === "otp" ? null : (
                            <Col xs={12} md={4} mdOffset={2} lg={3} lgOffset={3}>
                                <Field
                                    idForm="form.credential"
                                    name="credential"
                                    labelText={`form.credential.${credentialType}.label`}
                                    placeholderText={credentialType === "otp" ? "Ej: 120573" : "Ej: Ju@n_123"}
                                    component={Credential}
                                    type={credentialType}
                                    disabled={isSubmitting || isLoading}
                                />
                            </Col>
                        )}
                        <Col xs={12} md={4} mdOffset={6} lg={3} lgOffset={6} className="align-items-end">
                            <div className="form-group hidden-label">
                                <div className="button-group">
                                    <Button
                                        block
                                        type="submit"
                                        label={customSubmitLabel || "global.send"}
                                        bsStyle="primary"
                                        loading={isSubmitting || isLoading}
                                        disabled={handleActivation(values)}
                                    />
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Form>
            )}
        </Formik>
    );
};

GeneralCredentialForm.propTypes = {
    action: func.isRequired,
    idActivity: string.isRequired,
    transactionData: shape({}).isRequired,
    actionOnReload: func,
    isDisabled: bool,
    hasTerms: bool,
    hideIndications: bool,
    customIndications: string,
    signData: instanceOf(Array),
    deviceStatus: string.isRequired,
    tokenInfo: shape({}).isRequired,
    isLoading: bool,
    hideButton: bool,
    messageSnackBar: string,
    customClass: string,
    customSubmitLabel: string,
    isCancel: bool,
    type: string,
    disableContinueIfEmpty: bool,
};

GeneralCredentialForm.defaultProps = {
    actionOnReload: null,
    isDisabled: false,
    hasTerms: false,
    hideIndications: false,
    customIndications: "",
    signData: [],
    isLoading: false,
    hideButton: false,
    messageSnackBar: null,
    customClass: "",
    customSubmitLabel: null,
    isCancel: false,
    type: null,
    disableContinueIfEmpty: false,
};
const mapStateToProps = (state) => ({
    tokenInfo: selectors.getTokenInfo(state),
    deviceStatus: selectors.getDeviceStatus(state),
});
export default connect(mapStateToProps)(GeneralCredentialForm);
